/* FENLO native presentation runtime. No PageFly runtime, customer writes or cart requests. */
(() => {
  'use strict';
  if (window.FENLONativeUI) { window.FENLONativeUI.init(); return; }
  const ROOT = '[data-fenlo-native],.fenlo-native-page';
  const MEDIA = '[data-pf-type="ProductMedia"],[data-pf-type="ProductMedia2"],[data-pf-type="ProductMedia3"]';
  const TABS = '[data-pf-type="Tabs"],[data-pf-type="Tabs3"]';
  const BOX = '[data-pf-type="ProductBox"],[data-pf-type="ProductBox2"]';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const roots = new WeakMap(), sliders = new WeakMap(), galleries = new WeakMap();
  const all = (el, selector) => Array.from(el.querySelectorAll(selector));
  const within = (el, selector, owner) => all(el, selector).filter(node => node.closest(owner) === el);
  const visible = el => el.isConnected && el.getClientRects().length > 0 && !el.closest('[hidden],[inert],[aria-hidden="true"]');
  const number = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const device = () => innerWidth < 768 ? 'mobile' : innerWidth < 1025 ? 'tablet' : innerWidth < 1200 ? 'laptop' : 'all';
  const responsive = (value, fallback) => value && typeof value === 'object' ? value[device()] ?? value.all ?? fallback : value ?? fallback;
  function parse(value) {
    try { return JSON.parse(value || '{}'); } catch (_) {
      // Published list settings use single-quoted JSON with no user code. Never evaluate it.
      try { return JSON.parse((value || '{}').replace(/'/g, '"')); } catch (_) { return {}; }
    }
  }
  function config(el) {
    if (!el) return {};
    const local = el.closest('[data-fenlo-settings]');
    const settings = local ? parse(local.dataset.fenloSettings) : window.fenloNativeSettings || {};
    const data = settings.elementData || settings;
    const key = Array.from(el.classList).find(name => /^pf-\d+_$/.test(name));
    return { ...(data[key] || {}), ...parse(el.dataset.fenloUi), ...parse(el.dataset.slider) };
  }
  function emit(el, name, detail) { el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail })); }
  function pauseMedia(el) {
    all(el, 'video,audio').forEach(media => media.pause());
    all(el, 'iframe').forEach(frame => {
      try {
        const url = new URL(frame.src, location.href);
        if (/^(www\.)?youtube(-nocookie)?\.com$/.test(url.hostname)) {
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), url.origin);
        } else if (url.hostname === 'player.vimeo.com') {
          frame.contentWindow?.postMessage({ method: 'pause' }, url.origin);
        }
      } catch (_) { /* Unloaded frames have no player to pause. */ }
    });
  }
  function loadMedia(el) {
    if (!visible(el)) return;
    const hydratedVideos = new Set();
    const targets = [el, ...all(el, '.pf-bg-lazy,img[data-src],img[data-srcset],video[data-src],source[data-src],iframe[data-fenlo-lazy-src]')];
    targets.forEach(node => {
      if (!visible(node) && node.tagName !== 'SOURCE') return;
      node.classList.remove('pf-bg-lazy');
      if (/^(IMG|VIDEO|SOURCE)$/.test(node.tagName)) {
        if (node.dataset.src && !(node.tagName === 'IMG' && node.dataset.action === 'modal')) {
          node.src = node.dataset.src; delete node.dataset.src;
          if (node.tagName === 'VIDEO') hydratedVideos.add(node);
          if (node.tagName === 'SOURCE' && node.parentElement?.tagName === 'VIDEO') hydratedVideos.add(node.parentElement);
        }
        if (node.dataset.srcset) { node.srcset = node.dataset.srcset; delete node.dataset.srcset; }
        node.classList.remove('pf-visibility-hidden');
      }
      if (node.tagName === 'IFRAME' && node.dataset.fenloLazySrc) {
        node.removeAttribute('srcdoc');
        node.src = node.dataset.fenloLazySrc;
        delete node.dataset.fenloLazySrc;
      }
      if (node.tagName === 'VIDEO' && (node.autoplay || node.dataset.autoplay === 'true') && !motion.matches) {
        node.muted = node.hasAttribute('muted') || node.muted;
        node.play()?.catch(() => { /* Native controls/play button remain available. */ });
      }
    });
    hydratedVideos.forEach(video => {
      video.load();
      if ((video.autoplay || video.dataset.autoplay === 'true') && !motion.matches) video.play()?.catch(() => {});
    });
  }
  function button(el, label, signal, action) {
    if (!el) return;
    if (!/^(BUTTON|A|INPUT)$/.test(el.tagName)) {
      el.setAttribute('role', 'button');
      el.tabIndex = 0;
      el.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); action(event); }
      }, { signal });
    }
    if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', label);
    el.addEventListener('click', action, { signal });
  }
  function fadeController(track, signal, onChange) {
    const items = Array.from(track.children).filter(item => item.classList.contains('splide__slide'));
    if (!items.length) return null;
    const shell = track.closest('.splide');
    const settings = parse(shell.dataset.splide);
    // The exact source v1 dependency is Splide 2.4.21: fade, 400ms, this easing;
    // drag/arrows are disabled by the source ProductMedia initializer.
    const duration = number(settings.speed, 400);
    const easing = settings.easing || 'cubic-bezier(.42,.65,.27,.99)';
    shell.classList.add('is-initialized', 'is-active', 'splide--fade');
    shell.style.visibility = 'visible';
    track.style.display = 'block'; track.style.position = 'relative'; track.style.transform = 'none';
    track.dataset.fenloFade = 'true';
    if (!shell.hasAttribute('tabindex')) shell.tabIndex = 0;
    shell.style.touchAction = 'pan-y';
    let index = 0, touch = null, suppressClickUntil = 0;
    function refresh() {
      const main = shell.closest('[data-pf-type="MainMedia"]');
      const prefix = { all: 'pf-lg-', laptop: 'pf-md-', tablet: 'pf-sm-', mobile: 'pf-' }[device()];
      if (main?.classList.contains(`${prefix}media-fixed-height`)) return;
      const height = items[index].getBoundingClientRect().height;
      if (height > 0) { track.style.transition = motion.matches ? 'none' : 'height 0.5s ease'; track.style.height = `${height}px`; }
    }
    function go(next, instant = false) {
      index = Math.max(0, Math.min(items.length - 1, next));
      items.forEach((item, i) => {
        const active = i === index;
        item.style.width = '100%'; item.style.left = '0'; item.style.top = '0';
        item.style.transition = `opacity ${instant || motion.matches ? 0 : duration}ms ${easing}`;
        item.style.position = active ? 'relative' : 'absolute'; item.style.opacity = active ? '1' : '0';
        item.style.pointerEvents = active ? '' : 'none';
        item.classList.toggle('is-active', active); item.classList.toggle('is-visible', active);
      });
      track.dataset.fenloIndex = String(index);
      refresh();
      onChange(index);
    }
    shell.addEventListener('keydown', event => {
      if (event.target.closest('a,button,input,select,textarea,video')) return;
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault(); go(event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : index + (event.key === 'ArrowLeft' ? -1 : 1));
      }
    }, { signal });
    shell.addEventListener('pointerdown', event => {
      if (event.pointerType === 'touch') touch = { x: event.clientX, y: event.clientY, started: performance.now() };
    }, { signal, passive: true });
    shell.addEventListener('pointerup', event => {
      if (!touch) return;
      const distance = event.clientX - touch.x, verticalDistance = Math.abs(event.clientY - touch.y);
      const velocity = Math.abs(distance) / Math.max(1, performance.now() - touch.started);
      if (Math.abs(distance) > verticalDistance && (Math.abs(distance) >= 150 || (Math.abs(distance) > 30 && velocity > 0.6))) {
        go(index + (distance < 0 ? 1 : -1)); suppressClickUntil = performance.now() + 300;
      }
      touch = null;
    }, { signal, passive: true });
    shell.addEventListener('pointercancel', () => { touch = null; }, { signal });
    shell.addEventListener('click', event => {
      if (performance.now() < suppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { signal, capture: true });
    track.addEventListener('load', refresh, { signal, capture: true });
    const observer = window.ResizeObserver ? new ResizeObserver(refresh) : null;
    items.forEach(item => observer?.observe(item));
    signal.addEventListener('abort', () => observer?.disconnect(), { once: true });
    go(0, true);
    return { go, items, get index() { return index; }, refresh };
  }
  function trackController(track, settings, signal, onChange) {
    if (sliders.has(track)) return sliders.get(track);
    const slideSelector = '.pf-slide,.pf-slide-main-media,.pf-slide-list-media,.splide__slide';
    const items = Array.from(track.children).filter(node => node.matches(slideSelector));
    if (!items.length) return null;
    const shell = track.classList.contains('splide__list') ? track.closest('.splide') : track.parentElement;
    const viewport = track.classList.contains('splide__list') ? track.parentElement : track;
    const isMain = Boolean(track.closest('[data-pf-type="MainMedia"],[data-pf-type="MediaMain"],[data-pf-type="MediaMain2"]'));
    if (track.classList.contains('splide__list')) {
      shell.classList.add('is-initialized', 'is-active');
      shell.style.visibility = 'visible';
      track.style.display = 'flex';
      track.style.transform = 'none';
      if (isMain) items.forEach(item => { item.style.flex = '0 0 100%'; });
    }
    const vertical = () => getComputedStyle(track).flexDirection.startsWith('column');
    viewport.style.setProperty('scrollbar-width', 'none');
    viewport.style.scrollSnapType = vertical() ? 'y mandatory' : 'x mandatory';
    viewport.style.overflowX = vertical() ? 'hidden' : 'auto';
    if (vertical()) viewport.style.overflowY = 'auto';
    viewport.dataset.fenloTrack = 'true';
    if (!viewport.hasAttribute('tabindex')) viewport.tabIndex = 0;
    items.forEach(item => { item.style.scrollSnapAlign = 'start'; });
    let index = 0, timer = 0, paused = false, frame = 0;
    const extent = () => vertical() ? viewport.clientHeight : viewport.clientWidth;
    const position = () => vertical() ? viewport.scrollTop : viewport.scrollLeft;
    const offset = item => {
      const a = item.getBoundingClientRect(), b = viewport.getBoundingClientRect();
      return position() + (vertical() ? a.top - b.top : a.left - b.left);
    };
    const max = () => Math.max(0, (vertical() ? viewport.scrollHeight : viewport.scrollWidth) - extent());
    function sizeLegacyThumbs() {
      if (!settings.fenloLegacyThumbs || !viewport.clientWidth) return;
      const count = Math.max(1, number(responsive(settings.slidesToShow, 5), 5));
      const gap = parseFloat(responsive(settings.spacing, '10px')) || 0;
      const size = number(settings.fixedWidth, Math.floor(viewport.clientWidth / count) - gap * (count - 1) / count);
      items.forEach((item, i) => {
        item.style.flex = `0 0 ${size}px`; item.style.width = `${size}px`;
        item.style.height = `${number(settings.fixedHeight, size)}px`;
        item.style.marginRight = i === items.length - 1 ? '0px' : `${gap}px`;
      });
    }
    const pageStep = () => Math.max(1, number(responsive(settings.slidesToScroll, 1), 1));
    const controls = selector => all(shell, selector).filter(el => {
      const owner = el.closest('.pf-slider,.pf-media-slider,.splide');
      return owner === track || owner === shell || (!owner && el.parentElement === shell);
    });
    const previous = controls('.pf-slider-prev,.splide__arrow--prev');
    const next = controls('.pf-slider-next,.splide__arrow--next');
    const nav = controls('.pf-slider-nav,.splide__pagination')[0];
    let dots = [];
    function update() {
      const pos = position();
      let nearest = 0, distance = Infinity;
      items.forEach((item, i) => { const delta = Math.abs(Math.min(max(), offset(item)) - pos); if (delta < distance) { distance = delta; nearest = i; } });
      const changed = nearest !== index; index = nearest;
      track.dataset.fenloIndex = String(index);
      previous.forEach(el => { el.disabled = !settings.loop && pos < 2; el.style.visibility = settings.navStyle !== 'none' && max() > 1 && !el.disabled ? 'visible' : 'hidden'; });
      next.forEach(el => { el.disabled = !settings.loop && pos >= max() - 2; el.style.visibility = settings.navStyle !== 'none' && max() > 1 && !el.disabled ? 'visible' : 'hidden'; });
      dots.forEach((dot, i) => { const active = i === Math.floor(index / pageStep()); dot.classList.toggle('active', active); dot.classList.toggle('pf-active', active); dot.setAttribute('aria-current', active ? 'true' : 'false'); });
      if (changed || track.dataset.fenloInitialized !== 'true') onChange?.(index);
      track.dataset.fenloInitialized = 'true';
    }
    function go(target, instant = false) {
      const last = items.length - 1;
      target = settings.loop ? (target < 0 ? last : target > last ? 0 : target) : Math.max(0, Math.min(last, target));
      const destination = Math.min(max(), offset(items[target]));
      viewport.scrollTo({ [vertical() ? 'top' : 'left']: destination, behavior: instant || motion.matches ? 'instant' : 'smooth' });
      if (instant || motion.matches) update();
    }
    function step(delta) {
      if (delta > 0 && position() >= max() - 2 && settings.loop) go(0);
      else if (delta < 0 && position() < 2 && settings.loop) go(items.length - 1);
      else go(index + delta * pageStep());
    }
    const useControls = settings.navStyle !== 'none';
    [...previous, ...next].forEach(el => { el.style.visibility = useControls && items.length > 1 ? 'visible' : 'hidden'; });
    previous.forEach(el => button(el, 'Previous slide', signal, event => { event.preventDefault(); step(-1); }));
    next.forEach(el => button(el, 'Next slide', signal, event => { event.preventDefault(); step(1); }));
    function pagination() {
      if (!nav || settings.paginationStyle === 'none') return;
      const isSlideshow = Boolean(track.closest('[data-pf-type="Slideshow"]'));
      const count = max() <= 1 && !isSlideshow ? 0 : Math.max(1, Math.ceil((items.length - number(responsive(settings.slidesToShow, 1), 1)) / pageStep()) + 1);
      if (dots.length === count) return;
      dots.forEach(dot => dot.remove());
      dots = Array.from({ length: count }, (_, i) => {
        const dot = document.createElement('button'); dot.type = 'button'; dot.className = 'pf-slider-dot';
        dot.dataset.fenloDot = 'true';
        dot.setAttribute('aria-label', `Go to slide ${i * pageStep() + 1}`);
        dot.addEventListener('click', () => { go(i * pageStep()); }, { signal });
        nav.append(dot); return dot;
      });
    }
    viewport.addEventListener('keydown', event => {
      if (event.target !== viewport && event.target.closest('button,a,input,select,textarea,video')) return;
      if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'Home') go(0); else if (event.key === 'End') go(items.length - 1);
        else step(['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1);
      }
    }, { signal });
    viewport.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); }, { signal, passive: true });
    // Touch and trackpad scrolling remain native. Mouse dragging supplies the same slide interaction.
    let drag = null, suppressClickUntil = 0;
    items.forEach(item => all(item, 'img').forEach(img => { img.draggable = false; }));
    viewport.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select,textarea,video,iframe')) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, position: position(), moved: false, snap: viewport.style.scrollSnapType, selection: viewport.style.userSelect };
    }, { signal });
    viewport.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const distance = vertical() ? event.clientY - drag.y : event.clientX - drag.x;
      if (!drag.moved && Math.abs(distance) < 6) return;
      if (!drag.moved) {
        drag.moved = true;
        try { viewport.setPointerCapture(event.pointerId); } catch (_) { /* Pointer may already be released. */ }
        viewport.style.scrollSnapType = 'none'; viewport.style.userSelect = 'none';
      }
      event.preventDefault();
      if (vertical()) viewport.scrollTop = drag.position - distance; else viewport.scrollLeft = drag.position - distance;
    }, { signal });
    const endDrag = event => {
      if (!drag || event.pointerId !== drag.id) return;
      if (drag.moved) {
        suppressClickUntil = performance.now() + 250;
        viewport.style.scrollSnapType = drag.snap; viewport.style.userSelect = drag.selection;
        if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        update(); go(index);
      }
      drag = null;
    };
    viewport.addEventListener('pointerup', endDrag, { signal });
    viewport.addEventListener('pointercancel', endDrag, { signal });
    viewport.addEventListener('click', event => {
      if (performance.now() < suppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { signal, capture: true });
    shell.addEventListener('mouseenter', () => { paused = Boolean(settings.pauseOnHover); }, { signal });
    shell.addEventListener('mouseleave', () => { paused = false; }, { signal });
    function autoplay() {
      clearTimeout(timer);
      if (!settings.autoPlay) return;
      timer = setTimeout(() => {
        if (!document.hidden && !motion.matches && !paused && visible(track) && !shell.contains(document.activeElement)) {
          const rect = track.getBoundingClientRect();
          if (rect.bottom > 0 && rect.top < innerHeight && max() > 1) step(1);
        }
        autoplay();
      }, Math.max(250, number(settings.autoPlayDelay, 3000)));
    }
    const resize = () => { sizeLegacyThumbs(); pagination(); update(); };
    const observer = window.ResizeObserver ? new ResizeObserver(resize) : null;
    observer?.observe(viewport);
    signal.addEventListener('abort', () => { clearTimeout(timer); cancelAnimationFrame(frame); observer?.disconnect(); sliders.delete(track); }, { once: true });
    sizeLegacyThumbs(); pagination(); update(); autoplay();
    const api = { go, items, get index() { return index; }, refresh: resize };
    sliders.set(track, api); return api;
  }
  function setupGallery(gallery, signal) {
    if (galleries.has(gallery)) return;
    const main = gallery.querySelector('[data-pf-type="MediaMain2"],[data-pf-type="MediaMain"],[data-pf-type="MainMedia"]');
    const list = gallery.querySelector('[data-pf-type="MediaList2"],[data-pf-type="ProductMediaList"]');
    const track = main?.querySelector('.pf-media-slider,.splide__list');
    if (!track) return;
    const settings = config(main), thumbs = list ? all(list, '[data-img-id],.splide__slide') : [];
    const itemId = item => String(item.dataset.mediaId || item.dataset.imgId || item.querySelector('[data-media-id]')?.dataset.mediaId || '');
    const itemSrc = item => item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '';
    let api;
    function changed(index) {
      const items = Array.from(track.children).filter(item => item.matches('.pf-slide-main-media,.splide__slide'));
      const selected = items[index]; if (!selected) return;
      const id = itemId(selected);
      items.forEach((item, i) => { item.setAttribute('aria-hidden', String(i !== index)); if (i !== index) pauseMedia(item); });
      thumbs.forEach((thumb, i) => { const active = id ? itemId(thumb) === id : i === index; thumb.classList.toggle('active', active); thumb.classList.toggle('is-active', active); thumb.setAttribute('aria-current', String(active)); });
      gallery.dataset.fenloMediaId = id;
      loadMedia(selected);
      emit(gallery, 'fenlo:media-change', { mediaId: id, index, src: itemSrc(selected) });
    }
    api = main.dataset.pfType === 'MainMedia' ? fadeController(track, signal, changed) : trackController(track, settings, signal, changed);
    if (!api) return;
    // Source v1 uses --ratio only as a pre-initialization placeholder. Keeping it adds
    // its padding to the loaded slide height (360px + 360px on a 360px product card).
    main.style.removeProperty('--ratio');
    gallery.classList.remove('product-media-loading', 'pf-splide-loading');
    all(gallery, '.product-media-loading,.pf-splide-loading').forEach(el => el.classList.remove('product-media-loading', 'pf-splide-loading'));
    api.refresh();
    thumbs.forEach((thumb, i) => button(thumb, `View media ${i + 1}`, signal, event => {
      event.preventDefault();
      const found = api.items.findIndex(item => itemId(item) === itemId(thumb));
      api.go(found >= 0 ? found : i);
    }));
    const thumbTrack = list?.querySelector('.pf-media-slider,.splide__list');
    if (thumbTrack) {
      const legacy = list.dataset.pfType === 'ProductMediaList';
      const source = legacy ? parse(thumbTrack.closest('.splide')?.dataset.splide) : {};
      const ownerSettings = config(gallery);
      trackController(thumbTrack, { ...source, ...config(list), ...(legacy ? {
        fenloLegacyThumbs: true,
        slidesToShow: ownerSettings.limit || source.perPage || 5,
        spacing: ownerSettings.spacing || '10px',
        navStyle: source.arrows === false ? 'none' : 'nav-style-1'
      } : {}) }, signal);
    }
    // A gallery's existing mask is the user's play gesture; never replace video/model markup.
    all(gallery, '.pf-mask,.pf-mask-iframe').forEach(mask => button(mask, 'Play product media', signal, () => {
      const wrapper = mask.closest('.pf-media-wrapper') || mask.parentElement;
      loadMedia(wrapper);
      const video = wrapper.querySelector('video');
      if (video) { video.controls = true; video.play()?.catch(() => {}); }
      mask.style.display = 'none';
    }));
    if (number(({ ...config(gallery), ...settings }).hoverAction, 0) === 1) {
      api.items.forEach(item => {
        const img = Array.from(item.children).find(el => el.tagName === 'IMG');
        if (!img || item.dataset.mediaType !== 'image') return;
        const original = { opacity: img.style.opacity, image: item.style.backgroundImage, size: item.style.backgroundSize, repeat: item.style.backgroundRepeat, position: item.style.backgroundPosition };
        const restore = () => {
          img.style.opacity = original.opacity; item.style.backgroundImage = original.image;
          item.style.backgroundSize = original.size; item.style.backgroundRepeat = original.repeat; item.style.backgroundPosition = original.position;
        };
        item.addEventListener('mouseenter', () => {
          if (innerWidth < 1025) return;
          item.style.backgroundImage = `url("${(img.currentSrc || img.src).replace(/"/g, '%22')}")`;
          item.style.backgroundRepeat = 'no-repeat'; item.style.backgroundSize = '200%'; img.style.opacity = '0';
        }, { signal });
        item.addEventListener('mousemove', event => {
          if (innerWidth < 1025 || img.style.opacity !== '0') return;
          const rect = item.getBoundingClientRect();
          item.style.backgroundPosition = `${(event.clientX - rect.left) / rect.width * 100}% ${(event.clientY - rect.top) / rect.height * 100}%`;
        }, { signal });
        item.addEventListener('mouseleave', restore, { signal });
        gallery.addEventListener('fenlo:media-change', restore, { signal });
      });
    }
    galleries.set(gallery, api);
    signal.addEventListener('abort', () => galleries.delete(gallery), { once: true });
    const initialId = track.dataset.fenloSelectedMedia;
    const initialIndex = initialId ? api.items.findIndex(item => itemId(item) === initialId) : 0;
    if (initialIndex > 0) api.go(initialIndex, true);
    else changed(0);
  }
  function setupTabs(tabs, signal, activate) {
    const modern = tabs.dataset.pfType === 'Tabs3';
    const headers = within(tabs, modern ? 'label[data-pf-type="TabHeader3"]' : '[data-pf-type="TabHeader2"],[data-pf-type="TabHeader"]', TABS);
    const panels = within(tabs, modern ? '.pf-tab3-content-container' : '[data-pf-type="TabsContent"]', TABS);
    if (!headers.length || panels.length !== headers.length) return;
    const menu = within(tabs, modern ? '[data-pf-type="TabsMenu3"]' : '[data-pf-type="TabsMenu"]', TABS)[0];
    menu?.setAttribute('role', 'tablist');
    const dropdown = within(tabs, '[data-pf-type="DropdownButton"]', TABS)[0];
    let selected = Math.max(0, headers.findIndex(header => header.dataset.pfTabActive === 'true'));
    const set = (index, focus = false) => {
      selected = (index + headers.length) % headers.length;
      headers.forEach((header, i) => {
        const active = i === selected;
        header.dataset.pfTabActive = String(active); header.setAttribute('aria-selected', String(active)); header.tabIndex = active ? 0 : -1;
        if (header.htmlFor) { const radio = document.getElementById(header.htmlFor); if (radio && tabs.contains(radio)) radio.checked = active; }
        panels[i].setAttribute('aria-hidden', String(!active));
        if (modern) {
          // Tabs3's original checked-radio selectors animate CSS grid rows from 0fr to 1fr.
          // Keep that layout and any transition intact; inert excludes collapsed controls/media.
          panels[i].inert = !active;
        } else {
          panels[i].hidden = !active; panels[i].style.display = active ? 'block' : 'none';
        }
        if (!active) pauseMedia(panels[i]);
      });
      if (dropdown) {
        const text = dropdown.querySelector('.pf-tab-dropdown-text'); if (text) text.textContent = headers[selected].textContent;
        dropdown.setAttribute('aria-expanded', 'false'); menu?.classList.add('pf-tab-menu-hide');
      }
      if (focus) headers[selected].focus();
      activate(panels[selected]);
      emit(tabs, 'fenlo:tab-change', { index: selected });
    };
    headers.forEach((header, index) => {
      header.setAttribute('role', 'tab');
      if (!header.id) header.id = `fenlo-tab-${crypto.randomUUID()}`;
      if (!panels[index].id) panels[index].id = `${header.id}-panel`;
      header.setAttribute('aria-controls', panels[index].id);
      panels[index].setAttribute('role', 'tabpanel'); panels[index].setAttribute('aria-labelledby', header.id);
      header.addEventListener('click', event => { event.preventDefault(); set(index); }, { signal });
      header.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          const target = event.key === 'Home' ? 0 : event.key === 'End' ? headers.length - 1 : ['ArrowLeft', 'ArrowUp'].includes(event.key) ? selected - 1 : ['ArrowRight', 'ArrowDown'].includes(event.key) ? selected + 1 : index;
          set(target, true);
        }
      }, { signal });
    });
    dropdown?.addEventListener('click', () => {
      const open = dropdown.getAttribute('aria-expanded') !== 'true';
      dropdown.setAttribute('aria-expanded', String(open)); menu?.classList.toggle('pf-tab-menu-hide', !open);
      dropdown.querySelector('.pf-tab-dropdown-icon')?.classList.toggle('pf-dropdown-collapse', !open);
    }, { signal });
    within(tabs, '.pf-tab-prev-btn,.pf-tab-next-btn', TABS).forEach(control => button(control, 'Scroll tabs', signal, event => {
      event.preventDefault(); menu?.scrollBy({ left: menu.clientWidth * (control.classList.contains('pf-tab-prev-btn') ? -1 : 1), behavior: motion.matches ? 'instant' : 'smooth' });
    }));
    set(selected);
  }
  function initRoot(root) {
    if (roots.has(root)) return;
    if (!document.getElementById('fenlo-native-gallery-structure')) {
      const css = document.createElement('style'); css.id = 'fenlo-native-gallery-structure';
      // Structural v1 slider styles formerly injected by its runtime. Low specificity lets
      // the preserved per-page arrow hover/color declarations retain their original priority.
      css.textContent = ':where([data-fenlo-native],.fenlo-native-page) .splide{position:relative}:where([data-fenlo-native],.fenlo-native-page) .splide__arrow{position:absolute;z-index:1;top:50%;transform:translateY(-50%);width:2em;height:2em;border-radius:2px;display:flex;align-items:center;justify-content:center;border:0;padding:0;background:#ccc}:where([data-fenlo-native],.fenlo-native-page) .splide__arrow svg{width:1.2em;height:1.2em}:where([data-fenlo-native],.fenlo-native-page) .splide__arrow--prev{left:1em}:where([data-fenlo-native],.fenlo-native-page) .splide__arrow--prev svg{transform:scaleX(-1)}:where([data-fenlo-native],.fenlo-native-page) .splide__arrow--next{right:1em}';
      document.head.append(css);
    }
    const controller = new AbortController(), { signal } = controller;
    roots.set(root, controller);
    let queued = 0, sectionScrollFrame = 0;
    const reveal = new Set();
    all(root, '[data-animation]').forEach(el => {
      if (!['fadeIn', 'fadeInUp', 'slideInUp'].includes(el.dataset.animation)) { el.style.visibility = 'visible'; return; }
      if (!motion.matches) { el.style.visibility = 'hidden'; reveal.add(el); }
      else el.style.visibility = 'visible';
    });
    function revealVisible() {
      reveal.forEach(el => {
        if (!visible(el)) return;
        const rect = el.getBoundingClientRect(), bottom = rect.height > innerHeight ? rect.top + innerHeight * 0.5 : rect.bottom;
        if (motion.matches || bottom + number(el.dataset.offset, 0) <= innerHeight) {
          el.style.visibility = 'visible'; reveal.delete(el);
          if (!motion.matches) {
            el.classList.add('pf-animated', el.dataset.animation);
            // The frozen theme's animation CSS supplies the original 1s/ease keyframes.
            el.addEventListener('animationend', () => { setTimeout(() => el.classList.remove('pf-animated'), 2000); }, { once: true, signal });
          }
        }
      });
    }
    const lazies = new Set(all(root, '.pf-bg-lazy,img[data-src],img[data-srcset],video[data-src],source[data-src],iframe[data-fenlo-lazy-src]').map(el => el.tagName === 'SOURCE' ? el.parentElement : el));
    const lazyObserver = window.IntersectionObserver ? new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting && visible(entry.target)) { loadMedia(entry.target); lazyObserver.unobserve(entry.target); lazies.delete(entry.target); } });
    }, { rootMargin: '250px' }) : null;
    lazies.forEach(el => lazyObserver ? lazyObserver.observe(el) : loadMedia(el));
    function activate(el) {
      // Hidden-tab images and players wait until the chosen panel is actually shown.
      all(el, '.pf-bg-lazy,img[data-src],video[data-src],source[data-src],iframe[data-fenlo-lazy-src]').map(node => node.tagName === 'SOURCE' ? node.parentElement : node).forEach(node => {
        const rect = node.getBoundingClientRect(); if (visible(node) && rect.top < innerHeight + 250 && rect.bottom > -250) loadMedia(node);
      });
      revealVisible();
    }
    all(root, MEDIA).forEach(el => setupGallery(el, signal));
    all(root, '[data-pf-type="Slideshow"] > .pf-slider,.pf-slider[data-slider]').forEach(track => trackController(track, { ...config(track.parentElement), ...config(track) }, signal));
    all(root, TABS).forEach(el => setupTabs(el, signal, activate));
    all(root, '[data-to-section]').forEach(link => {
      button(link, link.getAttribute('aria-label') || link.title || link.alt || 'Go to section', signal, event => {
        const id = link.dataset.toSection;
        const target = all(root, '[data-section-id],[id],[class]').find(el => el.dataset.sectionId === id || el.id === id || el.classList.contains(id));
        if (!target) return;
        event.preventDefault(); event.stopPropagation();
        const offset = number(responsive(parse(link.dataset.offset), 0), 0);
        const start = window.scrollY, destination = start + target.getBoundingClientRect().top - offset;
        cancelAnimationFrame(sectionScrollFrame);
        if (motion.matches) { window.scrollTo({ top: destination, behavior: 'instant' }); return; }
        // Source section links use a 1000ms ease-out cubic motion, separate from slider scrolling.
        const started = performance.now();
        const tick = now => {
          const progress = Math.min(1, (now - started) / 1000);
          window.scrollTo({ top: start + (destination - start) * (1 - (1 - progress) ** 3), behavior: 'instant' });
          if (progress < 1) sectionScrollFrame = requestAnimationFrame(tick);
        };
        sectionScrollFrame = requestAnimationFrame(tick);
      });
      link.setAttribute('role', 'link');
    });
    all(root, '[data-action="modal"][data-popup-content="image"]').forEach(trigger => {
      button(trigger, trigger.dataset.imagePopupTitle || 'Enlarge image', signal, event => {
        event.preventDefault(); event.stopPropagation();
        if (!document.getElementById('fenlo-native-modal-style')) {
          const css = document.createElement('style'); css.id = 'fenlo-native-modal-style';
          css.textContent = '.fenlo-native-image-dialog::backdrop{background:rgba(0,0,0,.9)}.fenlo-native-image-dialog[open]{display:flex;flex-direction:column;align-items:flex-end}.fenlo-native-image-dialog{border:0;padding:0;background:transparent;overflow:hidden;max-width:90vw;max-height:min(calc(9/16*100vw),calc(100vh - 6px - 2em))}.fenlo-native-image-dialog>button{width:16px;height:16px;min-height:16px;margin:0 0 8px;padding:0;background:none;border:0;color:white;font-size:20px;line-height:16px;cursor:pointer}.fenlo-native-image-dialog>img{display:block;width:100%;height:calc(100% - 24px);margin:auto;max-width:90vw;max-height:90vh}';
          document.head.append(css);
        }
        const dialog = document.createElement('dialog'); dialog.className = 'fenlo-native-image-dialog';
        dialog.setAttribute('aria-label', trigger.dataset.imagePopupTitle || 'Image preview');
        dialog.style.width = trigger.dataset.width || '854px';
        dialog.style.height = `calc(${trigger.dataset.height || '480px'} + 24px)`;
        const close = document.createElement('button'); close.type = 'button'; close.textContent = '×'; close.setAttribute('aria-label', 'Close image');
        const img = document.createElement('img'); img.src = trigger.dataset.src || trigger.currentSrc || trigger.src;
        img.alt = trigger.dataset.imagePopupAlt || trigger.alt || ''; img.title = trigger.dataset.imagePopupTitle || trigger.title || '';
        img.style.objectFit = trigger.dataset.imagePopupObjFit || 'contain'; img.style.objectPosition = trigger.dataset.imagePopupObjPosition || 'center center';
        dialog.append(close, img); document.body.append(dialog);
        const dismiss = () => dialog.close();
        close.addEventListener('click', dismiss);
        dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss(); });
        dialog.addEventListener('click', event => { if (event.target === dialog) dismiss(); });
        dialog.addEventListener('close', () => { dialog.remove(); trigger.focus(); }, { once: true });
        signal.addEventListener('abort', () => dialog.remove(), { once: true });
        dialog.showModal(); close.focus();
      });
    });
    all(root, '.pf-play-button').forEach(control => {
      const wrapper = control.closest('.pf-video-wrapper') || control.parentElement, video = wrapper.querySelector('video');
      if (!video) return;
      button(control, 'Play video', signal, () => { loadMedia(wrapper); video.paused ? video.play()?.catch(() => {}) : video.pause(); });
      video.addEventListener('play', () => { control.style.opacity = '0'; control.setAttribute('aria-label', 'Pause video'); }, { signal });
      video.addEventListener('pause', () => { control.style.opacity = '1'; control.setAttribute('aria-label', 'Play video'); }, { signal });
      video.addEventListener('ended', () => { control.style.opacity = '1'; }, { signal });
    });
    all(root, '[data-pf-type^="Paragraph"] label[for]').forEach(label => button(label, 'Expand text', signal, event => {
      const checkbox = document.getElementById(label.htmlFor);
      if (checkbox?.type === 'checkbox' && root.contains(checkbox)) { event.preventDefault(); checkbox.checked = !checkbox.checked; label.setAttribute('aria-expanded', String(checkbox.checked)); }
    }));
    const parallax = all(root, '[data-parallax="true"]').map(el => ({ el, image: el.querySelector('.pf-parallax-img'), speed: number(el.dataset.parallaxSpeed, 4) / 10, position: el.style.backgroundPositionY }));
    function update() {
      queued = 0; revealVisible();
      parallax.forEach(({ el, image, speed, position }) => {
        if (image) image.style.opacity = '1';
        if (motion.matches) { if (image) image.style.transform = ''; else el.style.backgroundPositionY = position; return; }
        const rect = el.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < innerHeight) {
          const shift = (innerHeight / 2 - rect.top - rect.height / 2) * speed;
          if (image) image.style.transform = `translate3d(0,${shift}px,0)`;
          else if (getComputedStyle(el).backgroundImage !== 'none') el.style.backgroundPositionY = `calc(50% + ${shift}px)`;
        }
      });
    }
    const requestUpdate = () => { if (!queued) queued = requestAnimationFrame(update); };
    document.addEventListener('scroll', requestUpdate, { signal, passive: true, capture: true });
    window.addEventListener('resize', requestUpdate, { signal, passive: true });
    motion.addEventListener('change', requestUpdate, { signal });
    root.addEventListener('fenlo:media-select', event => {
      const owner = event.target.closest(BOX) || root, detail = event.detail || {};
      all(owner, MEDIA).forEach(gallery => {
        const api = galleries.get(gallery); if (!api) return;
        const target = api.items.findIndex(item => String(item.dataset.mediaId || item.dataset.imgId || item.querySelector('[data-media-id]')?.dataset.mediaId || '') === String(detail.mediaId));
        const bySrc = target >= 0 ? target : api.items.findIndex(item => detail.src && all(item, 'img').some(img => img.src.split('?')[0] === detail.src.split('?')[0]));
        if (bySrc >= 0) api.go(bySrc, true);
      });
    }, { signal });
    signal.addEventListener('abort', () => { lazyObserver?.disconnect(); cancelAnimationFrame(queued); cancelAnimationFrame(sectionScrollFrame); roots.delete(root); }, { once: true });
    root.dataset.fenloUiReady = 'true';
    update();
  }
  function init(container = document) {
    const elements = [...(container.matches?.(ROOT) ? [container] : []), ...all(container, ROOT)];
    elements.filter(el => !el.parentElement?.closest(ROOT)).forEach(initRoot);
  }
  window.FENLONativeUI = Object.freeze({ init, version: '1.0.0' });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [event.target, ...all(event.target, ROOT)].forEach(el => roots.get(el)?.abort());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
